import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import AdminLayout from './AdminLayout';
import '../App.css';

const Templates = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profileId, setProfileId] = useState(null);

  // Available templates with layout variations
  const templates = [
    {
      id: 'template1',
      name: 'Classic Center',
      description: 'Logo center, name below - Elegant purple gradient',
      preview: {
        headerGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        accentColor: '#667eea',
        bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        layout: 'center'
      },
      thumbnail: '🎨'
    },
    {
      id: 'template2',
      name: 'Professional Left',
      description: 'Logo left, name right - Corporate blue theme',
      preview: {
        headerGradient: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        accentColor: '#2a5298',
        bgColor: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        layout: 'left'
      },
      thumbnail: '💼'
    },
    {
      id: 'template3',
      name: 'Modern Right',
      description: 'Logo right, name left - Vibrant orange theme',
      preview: {
        headerGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        accentColor: '#f5576c',
        bgColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        layout: 'right'
      },
      thumbnail: '🔥'
    },
    {
      id: 'template4',
      name: 'Top Center',
      description: 'Logo top center, compact layout - Green nature theme',
      preview: {
        headerGradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        accentColor: '#11998e',
        bgColor: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        layout: 'top-center'
      },
      thumbnail: '🌿'
    },
    {
      id: 'template5',
      name: 'Split Layout',
      description: 'Logo and name side by side - Royal gold theme',
      preview: {
        headerGradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
        accentColor: '#f6d365',
        bgColor: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
        layout: 'split'
      },
      thumbnail: '👑'
    },
    {
      id: 'template6',
      name: 'Minimal Top',
      description: 'Small logo top left, name prominent - Ocean blue theme',
      preview: {
        headerGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        accentColor: '#4facfe',
        bgColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        layout: 'minimal'
      },
      thumbnail: '🌊'
    },
    {
      id: 'template7',
      name: 'Political Party Theme',
      description: '80% Orange & 20% Green - Perfect for political parties',
      preview: {
        headerGradient: 'linear-gradient(135deg, #ff8c00 0%, #ff7f00 20%, #ff6b00 40%, #ff5500 60%, #ff4500 80%, #22c55e 100%)',
        accentColor: '#ff8c00',
        bgColor: 'linear-gradient(135deg, #ff8c00 0%, #ff7f00 20%, #ff6b00 40%, #ff5500 60%, #ff4500 80%, #22c55e 100%)',
        layout: 'political'
      },
      thumbnail: '🏛️'
    }
  ];

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/profiles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        setProfileId(data.data[0].id);
        setSelectedTemplate(data.data[0].template_id || 'template1');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleTemplateSelect = async (templateId) => {
    if (!profileId) {
      setMessage({ type: 'error', text: 'Please create a profile first!' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}/template`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ template_id: templateId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSelectedTemplate(templateId);
        setMessage({ type: 'success', text: 'Template updated successfully! View your profile to see the changes.' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Error updating template' });
      }
    } catch (error) {
      console.error('Error updating template:', error);
      setMessage({ type: 'error', text: 'Error updating template. Please try again.' });
    } finally {
      setLoading(false);
    }
  };


  const menuItems = [
    { icon: '🏠', label: 'Dashboard', path: '/dashboard', active: false },
    { icon: '📊', label: 'My Profile', path: `/profile/${user?.username}`, active: false },
    { icon: '📝', label: 'Create Profile', path: '/create-profile', active: false },
    { icon: '✏️', label: 'Edit Profile', path: '/edit-profile', active: false },
    { icon: '🎨', label: 'Templates', path: '/templates', active: true },
    { icon: '📈', label: 'Analytics', path: '/analytics', active: false },
    { icon: '📧', label: 'Messages', path: '/messages', active: false },
    { icon: '⚙️', label: 'Settings', path: '/settings', active: false }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <AdminLayout pageTitle="Templates" pageSubtitle="Choose a template for your profile">
        <div className="user-dashboard-content" style={{ padding: '32px 40px' }}>
          <h1 className="dashboard-title" style={{ fontSize: '2rem', fontWeight: '700', color: '#fff', marginBottom: '8px' }}>
            Choose Your Profile Template
          </h1>
          <p style={{ color: '#94a3b8', marginBottom: '32px', fontSize: '1rem' }}>
            Select a template to customize your profile appearance. You can change it anytime.
          </p>

          {message.text && (
            <div className={`alert alert-${message.type === 'success' ? 'success' : 'error'}`} style={{
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              background: message.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${message.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              color: message.type === 'success' ? '#22c55e' : '#ef4444'
            }}>
              {message.text}
            </div>
          )}

          {!profileId && (
            <div style={{
              padding: '24px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              marginBottom: '32px',
              color: '#ef4444'
            }}>
              <strong>⚠️ No Profile Found:</strong> Please create a profile first before selecting a template.
              <Link to="/create-profile" style={{ 
                display: 'inline-block', 
                marginLeft: '12px', 
                color: '#667eea', 
                textDecoration: 'none',
                fontWeight: '600'
              }}>
                Create Profile →
              </Link>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {templates.map((template) => (
              <div
                key={template.id}
                style={{
                  background: '#1e293b',
                  borderRadius: '16px',
                  padding: '24px',
                  border: selectedTemplate === template.id ? '3px solid #667eea' : '2px solid #334155',
                  cursor: profileId ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s',
                  opacity: profileId ? 1 : 0.6,
                  position: 'relative'
                }}
                onClick={() => profileId && handleTemplateSelect(template.id)}
                onMouseEnter={(e) => {
                  if (profileId) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (profileId) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {selectedTemplate === template.id && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: '#667eea',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    ✓ Selected
                  </div>
                )}

                {/* Template Preview */}
                <div style={{
                  width: '100%',
                  height: '200px',
                  background: template.preview.bgColor,
                  borderRadius: '12px',
                  marginBottom: '16px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Mock Profile Preview */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    background: 'white',
                    height: '60%',
                    borderRadius: '12px 12px 0 0',
                    padding: '16px'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: template.preview.accentColor,
                      margin: '0 auto 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem'
                    }}>
                      {template.thumbnail}
                    </div>
                    <div style={{
                      height: '8px',
                      background: template.preview.accentColor,
                      borderRadius: '4px',
                      marginBottom: '8px',
                      width: '80%',
                      margin: '0 auto 8px'
                    }}></div>
                    <div style={{
                      height: '6px',
                      background: '#e2e8f0',
                      borderRadius: '4px',
                      width: '60%',
                      margin: '0 auto'
                    }}></div>
                  </div>
                </div>

                <h3 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: '600', marginBottom: '8px' }}>
                  {template.name}
                </h3>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '16px' }}>
                  {template.description}
                </p>

                <button
                  disabled={!profileId || loading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: selectedTemplate === template.id 
                      ? '#667eea' 
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: profileId ? 'pointer' : 'not-allowed',
                    opacity: profileId ? 1 : 0.5,
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    if (profileId) {
                      e.target.style.transform = 'scale(1.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (profileId) {
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {selectedTemplate === template.id ? 'Currently Selected' : 'Select Template'}
                </button>
              </div>
            ))}
          </div>

          {profileId && (
            <div style={{
              padding: '24px',
              background: '#1e293b',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#94a3b8', marginBottom: '16px' }}>
                Want to see how your profile looks with the selected template?
              </p>
              <Link
                to={`/profile/${user?.username}`}
                style={{
                  display: 'inline-block',
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                View My Profile →
              </Link>
            </div>
          )}
        </div>
    </AdminLayout>
  );
};

export default Templates;

